import { useState } from 'react';
import { Alert, Button, Card, Group, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconPlugConnected } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { SecretField } from '../config/SecretField';

/**
 * The CANNOT_RECEIVE remedy: sending works, incoming messages are being dropped.
 *
 * Two distinct causes, two distinct fixes, and conflating them is why the
 * current warning is accurate but unactionable:
 *
 *  - **A manual connection with no BYOC app secret.** Every inbound webhook
 *    arrives signed by the vendor's own Meta app; without that app's secret we
 *    cannot verify the signature, so we drop the message. The vendor sees a
 *    healthy connection and silence. Fix: two fields.
 *  - **Anything else** — usually a subscription that never completed or has
 *    lapsed. Fix: one button.
 *
 * The copy leads with the symptom the vendor can recognise ("messages aren't
 * arriving"), not the mechanism ("webhook signature verification failed").
 */
export interface ReceiveRepairCardProps {
  needsAppSecret: boolean;
  onRepaired: () => void;
}

export function ReceiveRepairCard({ needsAppSecret, onRepaired }: ReceiveRepairCardProps) {
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const utils = trpc.useUtils();

  const saveByoc = trpc.tenancy.settings.setMany.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Saved. Checking again…', color: 'green' });
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      onRepaired();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const subscribe = trpc.integrations.meta.webhooks.subscribe.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Subscribed. Checking again…', color: 'green' });
      onRepaired();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Messages people send you aren't arriving"
        >
          <Text size="sm">
            {needsAppSecret
              ? "You connected using your own Meta app, but we don't have that app's security key — so we can't confirm incoming messages really came from Meta, and we have to ignore them."
              : "We're not subscribed to receive your incoming messages yet. You can send, but replies won't reach your inbox."}
          </Text>
        </Alert>

        {needsAppSecret ? (
          <Stack gap="sm">
            <TextInput
              label="App ID"
              description="From your Meta app's basic settings."
              placeholder="e.g. 1234567890123456"
              value={appId}
              onChange={(e) => setAppId(e.currentTarget.value)}
            />
            <SecretField
              label="App secret"
              description="Stored encrypted. Only ever used to check that incoming messages came from Meta."
              present={false}
              value={appSecret}
              onChange={setAppSecret}
              name="appSecret"
            />
            <Group>
              <Button
                loading={saveByoc.isPending}
                disabled={!appId.trim() || !appSecret.trim()}
                onClick={() =>
                  saveByoc.mutate({
                    settings: {
                      meta_app_id_byoc: appId.trim(),
                      meta_app_secret_byoc: appSecret.trim(),
                    },
                  })
                }
              >
                Save and retry
              </Button>
            </Group>
          </Stack>
        ) : (
          <Group>
            <Button
              leftSection={<IconPlugConnected size={16} />}
              loading={subscribe.isPending}
              onClick={() => subscribe.mutate({ channel: 'whatsapp' })}
            >
              Start receiving messages
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
